/**
 * Whiskey Poker hand engine + full 50-hand Poli Poker match.
 *
 * ── TURN RULES (confirmed with Scott, 2026-09) ────────────────────────────
 *   • 5 seats. Dealer rotates each hand; first to act is left of the dealer.
 *   • Deal 5 cards each + 5 to the middle, all face DOWN. Remaining 22 cards
 *     form the stock ("bones"); the discard pile starts empty.
 *   • The middle is face down until a player REVEALs it (turns all 5 face up)
 *     or takes it blind (the "Double Dumper"). The first player dealt to MUST
 *     do one or the other — no passing, drawing or knocking with it face down
 *     (rule confirmed with Scott, 2026-10-27). Turning it face up is free: that
 *     player then swaps one card, draws from the bones, passes or knocks — but
 *     may NOT take all five on that same turn. Every other turn, anyone may
 *     take the whole middle, face up (confirmed with Scott, 2026-10-31; this
 *     replaces the 29 Oct "only blind" rule). Once revealed it stays face up.
 *     The dealer may never take the middle blind.
 *   • One action per turn: KEEP / REVEAL (free) / TAKE_MIDDLE / SWAP_ONE /
 *     BLIND / KNOCK. After a KNOCK every other player gets one last turn, then
 *     showdown. Nobody may knock while the middle is still face down.
 *   • There are NEVER ties: equal category+ranks break by suit of the top card.
 *
 * Controllers may be async (a human clicks buttons); an optional observer is
 * notified after every action and at showdown so a UI can render/animate.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { type Card, shuffledDeck, cardToString } from "./cards.ts";
import { evaluate, compareHands, type HandRank, HandCategory } from "./handEval.ts";
import { scoreHand, type HandScoreBreakdown } from "./scoring.ts";
import { STATE_CODES, STATE_BY_CODE } from "./states.ts";
import { rngFromState, type Rng } from "./rng.ts";
import { PEEK_COST, analysePeek, type PeekAnalysis } from "./peek.ts";

export type StateMethod = "CHOOSE" | "RANDOM";

export interface PlayerConfig {
  id: string;
  name: string;
  party: string;
  colour: string;
  isHuman?: boolean;
}

export type Action =
  | { type: "KEEP" }
  | { type: "REVEAL" }
  | { type: "KNOCK" }
  | { type: "TAKE_MIDDLE" }
  | { type: "SWAP_ONE"; give: number; take: number; undo?: boolean }
  | { type: "BLIND"; give: number };

export interface TurnView {
  handNumber: number;
  seat: number;
  hand: Card[];
  middle: (Card | null)[];
  middleRevealed: boolean;
  stockCount: number;
  discardCount: number;
  someoneKnocked: boolean;
  turnsTaken: number;
  targetState?: string;
  legal: Action["type"][];
  /** Everything else that is public at this moment (scores, states, turn order, knock status). */
  table: PublicTable;
}

/** Public facts about the table, the same for every player. No concealed cards. */
export interface PublicTable {
  stateMethod: StateMethod;
  dealerSeat: number;
  /** Seat that knocked this hand, or -1. */
  knockerSeat: number;
  /** After a knock: how many of the other players still have their last turn to come. */
  finalTurnsLeft: number;
  /** Turns each seat has taken this hand. */
  turnsTaken: number[];
  /** Passes in a row so far (5 ends the hand). */
  consecutiveKeeps: number;
  /** Actions so far this hand (the engine stops a hand at MAX_ACTIONS_PER_HAND). */
  actionsThisHand: number;
  players: { seat: number; score: number; states: string[]; knocked: boolean }[];
  /** Unclaimed states, alphabetical (the draw order is not public). */
  unclaimed: string[];
  /** Hands still to play after this one. */
  handsAfterThis: number;
}

/**
 * What every controller is told after a move: only what the whole table could see.
 * A BLIND draw does not say which card was discarded (it goes face down), nor its position.
 */
export interface PublicEvent {
  handNumber: number;
  seat: number;
  action: { type: Action["type"] };
  /** The middle as the table saw it before and after the move (null = face down). */
  middleBefore: (Card | null)[];
  middleAfter: (Card | null)[];
  stockCount: number;
  discardCount: number;
  table: PublicTable;
}

/** Public, no-hidden-info snapshot of the table for observers/UI. */
export interface PublicPlayer {
  seat: number;
  id: string;
  name: string;
  party: string;
  colour: string;
  score: number;
  states: string[];
  cardCount: number;
  knocked: boolean;
}

export interface BoardSnapshot {
  handNumber: number;
  dealerSeat: number;
  currentSeat: number;
  middle: (Card | null)[];
  middleRevealed: boolean;
  stockCount: number;
  discardCount: number;
  someoneKnocked: boolean;
  /** The knocker's turn number when they knocked (1 = their first turn: a "quock"), or 0. */
  knockRound: number;
  targetState?: string;
  players: PublicPlayer[];
  /** Unclaimed states remaining, in draw order (for save/resume). */
  remainingPool: string[];
}

export interface GameObserver {
  onHandStart?(b: BoardSnapshot): void | Promise<void>;
  onAction?(e: { seat: number; action: Action; board: BoardSnapshot }): void | Promise<void>;
  /** Hands turned up, BEFORE the winner picks a state: targetState is "" unless the state was fixed
   *  (random order), and no contig bonus is counted yet. onShowdown follows with the final record. */
  onHandReveal?(e: { record: HandRecord; board: BoardSnapshot }): void | Promise<void>;
  onShowdown?(e: { record: HandRecord; board: BoardSnapshot }): void | Promise<void>;
  onStateWon?(e: { seat: number; state: string; board: BoardSnapshot }): void | Promise<void>;
  /** After the last hand (and any last peeks): the winner is President and has named a capital (one of
   *  their own states; none if they hold no state). Ceremonial: scores and states don't change. */
  /** The President is about to name a capital from `owned` (everyone's screen goes to the map). */
  onCapitalChoosing?(e: { seat: number; owned: string[] }): void | Promise<void>;
  onPresident?(e: { seat: number; capital: string | null; standings: PlayerTotals[] }): void | Promise<void>;
  onGameEnd?(standings: PlayerTotals[]): void | Promise<void>;
}

/** A peek bought after a hand (see peek.ts). */
export interface PeekRecord { hand: number; seat: number; card: Card; analysis: PeekAnalysis;
  /** Taken off the totals in this run of the game (a resumed game charges a bought peek again once). */
  charged?: boolean }
export type PeekResult = { ok: true; peek: PeekRecord; charged: boolean; score: number } | { ok: false; reason: string };

export type AfterSwap = "END" | "KNOCK" | "UNDO";

export interface Controller {
  /** The card action for this turn (KEEP/REVEAL/SWAP_ONE/TAKE_MIDDLE/BLIND, or KNOCK to knock immediately). */
  decideAction(view: TurnView): Action | Promise<Action>;
  /** After a (non-knock) card action, may still knock to end the hand. */
  decideKnock?(view: TurnView): boolean | Promise<boolean>;
  /**
   * After swapping one card with the middle (used instead of decideKnock when present): end the turn,
   * knock, or swap that same card straight back. After an UNDO the turn starts again (decideAction),
   * so the player can pick a different card or do anything else (Scott, 2026-10-27).
   */
  decideAfterSwap?(view: TurnView, opts: { canUndo: boolean; canKnock: boolean }): AfterSwap | Promise<AfterSwap>;
  chooseState?(unclaimed: string[], owned: string[], view: { handNumber: number; table?: PublicTable; handPoints?: number[] }): string | Promise<string>;
  /** Told every move as the table saw it (including its own), after it happens. */
  onObserve?(e: PublicEvent): void;
  /** Told the showdown (all hands are turned up). */
  onHandEnd?(record: HandRecord): void;
  /** Told its five cards as soon as everyone is dealt — before anyone acts. */
  onDeal?(hand: Card[], handNumber: number): void | Promise<void>;
  /** Told its new five cards right after its own exchange (swap, bones, whole middle). */
  onOwnHand?(hand: Card[]): void | Promise<void>;
  chooseCapital?(owned: string[]): string | Promise<string>;
}

export interface PlayerHandResult {
  id: string;
  seat: number;
  cards: Card[];
  rank: HandRank;
  position: number;
  breakdown: HandScoreBreakdown;
}

export interface HandRecord {
  handNumber: number;
  dealerSeat: number;
  targetState: string;
  winnerId: string;
  winnerSeat: number;
  winningCategory: HandCategory;
  knockerSeat: number; // seat that knocked this hand, or -1
  knockRound: number;  // which round (1 = each player's 1st turn) the knock landed on, or 0 if nobody knocked
  results: PlayerHandResult[];
}

export interface ResumeState {
  startHand: number;
  dealerSeat: number;
  totals: { id: string; score: number; states: string[] }[];
  pool: string[];
  /** The shuffler's state as that hand began (PoliGame.handStartRng): the same hand is dealt again. */
  rng?: number;
  /** Every peek bought so far. One for the resumed hand is charged again when that hand ends. */
  peeks?: PeekRecord[];
}

export interface GameConfig {
  players: PlayerConfig[];
  stateMethod: StateMethod;
  seed?: number;
  /** Who deals the first hand (decided by drawing for the deal; draw.ts). Default seat 0. */
  firstDealer?: number;
  resume?: ResumeState;
}

export interface PlayerTotals {
  id: string;
  name: string;
  party: string;
  colour: string;
  score: number;
  states: string[];
}

const MAX_ACTIONS_PER_HAND = 80;
/** A peek as the buyer sees it (without the engine's own bookkeeping). */
const shown = ({ charged: _, ...p }: PeekRecord): PeekRecord => p;

export class PoliGame {
  readonly cfg: GameConfig;
  private readonly rng: Rng;
  private readonly controllers: Controller[];
  private readonly observer?: GameObserver;
  readonly totals: PlayerTotals[];
  readonly history: HandRecord[] = [];
  private pool: string[];
  private dealerSeat = 0;
  private startHand = 1;
  private readonly rngState: { a: number };
  /** The shuffler's state when the current hand began (before its deal) — saved so a resume re-deals it. */
  handStartRng = 0;
  /** Peeks bought this game. */
  readonly peeks: PeekRecord[] = [];
  /** The hand just finished, while its peeks can be bought (its results are up): the card on top of the
   *  bones after each seat's final turn (null: the bones were empty), and everyone's final cards. */
  private peekWindow: { hand: number; cards: (Card | null)[]; hands: Card[][]; winnerSeat: number; pending: number[] | null } | null = null;
  /** The President's capital, once named. */
  capital: string | null = null;

  constructor(cfg: GameConfig, controllers: Controller[], observer?: GameObserver) {
    if (cfg.players.length !== 5) throw new Error("Poli Poker requires exactly 5 players");
    if (controllers.length !== 5) throw new Error("Need one controller per player");
    this.cfg = cfg;
    this.controllers = controllers;
    this.observer = observer;
    this.rngState = { a: (cfg.resume?.rng ?? cfg.seed ?? Date.now()) >>> 0 };
    this.rng = rngFromState(this.rngState);
    this.totals = cfg.players.map((p) => ({
      id: p.id,
      name: p.name,
      party: p.party,
      colour: p.colour,
      score: 0,
      states: [],
    }));
    this.pool = cfg.resume?.rng !== undefined ? [] : this.rng.shuffle([...STATE_CODES]);
    if (cfg.firstDealer !== undefined && cfg.firstDealer >= 0 && cfg.firstDealer < 5) this.dealerSeat = cfg.firstDealer;
    if (cfg.resume) {
      const r = cfg.resume;
      for (const t of this.totals) {
        const s = r.totals.find((x) => x.id === t.id);
        if (s) { t.score = s.score; t.states = [...s.states]; }
      }
      this.pool = [...r.pool];
      this.dealerSeat = r.dealerSeat;
      this.startHand = Math.min(STATE_CODES.length, Math.max(1, r.startHand));
      // earlier hands' peeks are already in the saved totals; the resumed hand's are charged when it ends
      this.peeks.push(...(r.peeks ?? []).map((p) => ({ ...p, charged: p.hand < this.startHand })));
    }
  }

  async play(): Promise<PlayerTotals[]> {
    for (let h = this.startHand; h <= STATE_CODES.length; h++) {
      await this.playHand(h);
      this.dealerSeat = (this.dealerSeat + 1) % 5;
    }
    // Every last peek is in (the final results are closed): the standings are final. The winner is
    // President and names a capital from their own states.
    const standings = this.standings();
    const seat = this.totals.indexOf(standings[0]);
    const owned = [...this.totals[seat].states];
    if (owned.length) {
      await this.observer?.onCapitalChoosing?.({ seat, owned: [...owned] });
      const chosen = await this.controllers[seat].chooseCapital?.([...owned]);
      this.capital = chosen && owned.includes(chosen) ? chosen : owned[0];
    }
    await this.observer?.onPresident?.({ seat, capital: this.capital, standings });
    await this.observer?.onGameEnd?.(standings);
    return standings;
  }

  /**
   * Peek for `seat` while the hand's results are up: free, once a hand (asking again shows the same
   * card). Needs a card on the bones after that seat's last turn.
   */
  buyPeek(seat: number, hand: number): PeekResult {
    const w = this.peekWindow;
    if (!w || w.hand !== hand) return { ok: false, reason: "Peeks for that hand are closed." };
    const had = this.peeks.find((p) => p.hand === hand && p.seat === seat);
    if (had) return { ok: true, peek: shown(had), charged: false, score: this.peekPoints(seat) };
    const card = w.cards[seat];
    if (!card) return { ok: false, reason: "The bones were empty after your last turn, so there's no card to see." };
    const peek: PeekRecord = { hand, seat, card: { ...card }, analysis: analysePeek(w.hands[seat], card, w.hands[w.winnerSeat], seat === w.winnerSeat) };
    this.totals[seat].score -= PEEK_COST;
    peek.charged = true;
    this.peeks.push(peek);
    return { ok: true, peek: shown(peek), charged: true, score: this.peekPoints(seat) };
  }
  /** Can `seat` peek at the hand whose results are up? ("" = yes, or why not). */
  peekBlocked(seat: number, hand: number): string {
    const w = this.peekWindow;
    if (!w || w.hand !== hand) return "Peeks for that hand are closed.";
    if (this.peeks.some((p) => p.hand === hand && p.seat === seat)) return "";
    if (!w.cards[seat]) return "The bones were empty after your last turn, so there's no card to see.";
    return "";
  }
  /** Your match points counting the hand just shown (its points are added once the state is chosen). */
  private peekPoints(seat: number): number {
    return this.totals[seat].score + (this.peekWindow?.pending?.[seat] ?? 0);
  }

  standings(): PlayerTotals[] {
    return [...this.totals].sort((a, b) => b.score - a.score || b.states.length - a.states.length);
  }

  private async playHand(handNumber: number): Promise<void> {
    this.handStartRng = this.rngState.a >>> 0;
    const deck = shuffledDeck(this.rng);
    const hands: Card[][] = [[], [], [], [], []];
    for (let i = 0; i < 5; i++) for (let s = 0; s < 5; s++) hands[s].push(deck.pop()!);
    const middle: Card[] = [deck.pop()!, deck.pop()!, deck.pop()!, deck.pop()!, deck.pop()!];
    let middleRevealed = false;
    let stock = deck;
    const discard: Card[] = [];
    const turnsTaken = [0, 0, 0, 0, 0];
    const targetState = this.cfg.stateMethod === "RANDOM" ? this.pool[0] : undefined;

    let knockerSeat = -1;
    let knockRound = 0;
    let lastTurnsLeft = -1;
    let seat = (this.dealerSeat + 1) % 5;
    let actions = 0;
    let consecutiveKeeps = 0;
    /** This seat turned the middle over this turn, so may not take all five until its next turn. */
    let justRevealed = false;
    /** The card on top of the bones after each seat's latest turn (null: none there) — for peeks. */
    const peekCards: (Card | null)[] = [null, null, null, null, null];

    const ensureStock = (): boolean => {
      if (stock.length === 0 && discard.length > 0) stock = this.rng.shuffle(discard.splice(0));
      return stock.length > 0;
    };
    const legalFor = (): Action["type"][] => {
      // Face down: turn it over or take it blind — nothing else.
      if (!middleRevealed) return seat !== this.dealerSeat ? ["REVEAL", "TAKE_MIDDLE"] : ["REVEAL"];
      const l: Action["type"][] = justRevealed ? ["KEEP", "SWAP_ONE"] : ["KEEP", "SWAP_ONE", "TAKE_MIDDLE"];
      if (stock.length > 0 || discard.length > 0) l.push("BLIND");
      if (knockerSeat === -1) l.push("KNOCK");
      return l;
    };
    const table = (): PublicTable => ({
      stateMethod: this.cfg.stateMethod,
      dealerSeat: this.dealerSeat,
      knockerSeat,
      finalTurnsLeft: knockerSeat === -1 ? 0 : lastTurnsLeft,
      turnsTaken: [...turnsTaken],
      consecutiveKeeps,
      actionsThisHand: actions,
      players: this.totals.map((t, s) => ({ seat: s, score: t.score, states: [...t.states], knocked: s === knockerSeat })),
      unclaimed: [...this.pool].sort(),
      handsAfterThis: STATE_CODES.length - handNumber,
    });
    const shownMiddle = (): (Card | null)[] => middle.map((c) => (middleRevealed ? { ...c } : null));
    const tell = (s: number, type: Action["type"], before: (Card | null)[]): void => {
      const e: PublicEvent = { handNumber, seat: s, action: { type }, middleBefore: before, middleAfter: shownMiddle(),
        stockCount: stock.length, discardCount: discard.length, table: table() };
      for (const c of this.controllers) c.onObserve?.(e);
    };
    const view = (): TurnView => ({
      handNumber,
      seat,
      hand: hands[seat].map((c) => ({ ...c })),
      middle: middle.map((c) => (middleRevealed ? { ...c } : null)),
      middleRevealed,
      stockCount: stock.length,
      discardCount: discard.length,
      someoneKnocked: knockerSeat !== -1,
      turnsTaken: turnsTaken[seat],
      targetState,
      legal: legalFor(),
      table: table(),
    });
    const board = (): BoardSnapshot => ({
      handNumber,
      dealerSeat: this.dealerSeat,
      currentSeat: seat,
      middle: middle.map((c) => (middleRevealed ? { ...c } : null)),
      middleRevealed,
      stockCount: stock.length,
      discardCount: discard.length,
      someoneKnocked: knockerSeat !== -1,
      knockRound,
      targetState,
      players: this.cfg.players.map((p, s) => ({
        seat: s,
        id: p.id,
        name: p.name,
        party: p.party,
        colour: p.colour,
        score: this.totals[s].score,
        states: [...this.totals[s].states],
        cardCount: hands[s].length,
        knocked: s === knockerSeat,
      })),
      remainingPool: [...this.pool],
    });

    const apply = (action: Action): void => {
      switch (action.type) {
        case "KEEP":
        case "KNOCK":
          return;
        case "TAKE_MIDDLE": {
          const old = hands[seat];
          hands[seat] = middle.slice();
          for (let i = 0; i < 5; i++) middle[i] = old[i];
          middleRevealed = true;
          return;
        }
        case "SWAP_ONE": {
          const gi = clamp(action.give, 0, 4);
          const ti = clamp(action.take, 0, 4);
          const taken = middle[ti];
          middle[ti] = hands[seat][gi];
          hands[seat][gi] = taken;
          return;
        }
        case "BLIND": {
          if (!ensureStock()) return;
          const drawn = stock.pop()!;
          const gi = clamp(action.give, 0, 4);
          discard.push(hands[seat][gi]);
          hands[seat][gi] = drawn;
          return;
        }
      }
    };

    await this.observer?.onHandStart?.(board());
    for (let s = 0; s < 5; s++) await this.controllers[s].onDeal?.(hands[s].map((c) => ({ ...c })), handNumber);

    while (true) {
      const skipKnocker = knockerSeat !== -1 && seat === knockerSeat;
      if (!skipKnocker) {
        let action = await this.controllers[seat].decideAction(view());
        // Anything but a blind take while the middle is face down turns it over first, then asks again.
        if (mustReveal(action, middleRevealed, seat === this.dealerSeat)) {
          middleRevealed = true;
          justRevealed = true;
          tell(seat, "REVEAL", middle.map(() => null));
          await this.observer?.onAction?.({ seat, action: { type: "REVEAL" }, board: board() });
          action = await this.controllers[seat].decideAction(view());
        }
        action = legalize(action, { revealed: middleRevealed, knocked: knockerSeat !== -1, dealer: seat === this.dealerSeat, justRevealed });

        const ctl = this.controllers[seat];
        let wantsKnock = false;
        // A swap with the middle can be put straight back (that card, that spot), which hands the
        // player their turn again to pick differently — so at most one card is ever out of their hand.
        for (;;) {
          const before = shownMiddle();
          apply(action);
          if (action.type === "SWAP_ONE" || action.type === "TAKE_MIDDLE" || action.type === "BLIND")
            await ctl.onOwnHand?.(hands[seat].map((c) => ({ ...c })));
          turnsTaken[seat]++;
          if (action.type === "KNOCK" && knockerSeat === -1) {
            knockerSeat = seat;
            knockRound = turnsTaken[seat];
            lastTurnsLeft = 4;
          }
          consecutiveKeeps = action.type === "KEEP" ? consecutiveKeeps + 1 : 0;
          tell(seat, action.type, before);
          await this.observer?.onAction?.({ seat, action, board: board() });

          // After exchanging a card you may still knock to end the hand.
          const exchanged =
            action.type === "SWAP_ONE" || action.type === "TAKE_MIDDLE" || action.type === "BLIND";
          const canKnock = knockerSeat === -1 && middleRevealed;
          if (action.type === "SWAP_ONE" && ctl.decideAfterSwap) {
            const choice = await ctl.decideAfterSwap(view(), { canUndo: true, canKnock });
            if (choice === "UNDO") {
              const back: Action = { ...action, undo: true }; // the same swap again puts both cards back
              const beforeUndo = shownMiddle();
              apply(back);
              await ctl.onOwnHand?.(hands[seat].map((c) => ({ ...c })));
              turnsTaken[seat]--;
              tell(seat, "SWAP_ONE", beforeUndo);
              await this.observer?.onAction?.({ seat, action: back, board: board() });
              action = legalize(await ctl.decideAction(view()), { revealed: middleRevealed, knocked: knockerSeat !== -1, dealer: seat === this.dealerSeat, justRevealed });
              continue;
            }
            wantsKnock = canKnock && choice === "KNOCK";
          } else if (canKnock && exchanged && ctl.decideKnock) {
            wantsKnock = await ctl.decideKnock(view());
          }
          break;
        }
        if (wantsKnock) {
          knockerSeat = seat;
          knockRound = turnsTaken[seat];
          lastTurnsLeft = 4;
          consecutiveKeeps = 0;
          tell(seat, "KNOCK", shownMiddle());
          await this.observer?.onAction?.({ seat, action: { type: "KNOCK" }, board: board() });
        }
      }

      if (!skipKnocker) peekCards[seat] = stock.length ? { ...stock[stock.length - 1] } : null;
      actions++;
      if (knockerSeat !== -1) {
        if (seat !== knockerSeat) lastTurnsLeft--; // only the OTHER players' last turns count
        if (lastTurnsLeft <= 0) break;
      }
      if (consecutiveKeeps >= 5) break;
      if (actions >= MAX_ACTIONS_PER_HAND) break;
      seat = (seat + 1) % 5;
      justRevealed = false;
    }

    await this.resolveHand(handNumber, hands, board, targetState, knockerSeat, knockRound, table, peekCards);
  }

  private async resolveHand(
    handNumber: number,
    hands: Card[][],
    board: () => BoardSnapshot,
    fixedTarget?: string,
    knockerSeat = -1,
    knockRound = 0,
    table?: () => PublicTable,
    peekCards: (Card | null)[] = [null, null, null, null, null],
  ): Promise<void> {
    const ranked = hands
      .map((cards, seat) => ({ seat, cards, rank: evaluate(cards) }))
      .sort((a, b) => compareHands(b.rank, a.rank));

    const winner = ranked[0];
    const winnerTotals = this.totals[winner.seat];

    let wonState: string;
    // Points each seat scores this hand before any contig bonus — public once the hands are shown.
    const handPoints = [0, 0, 0, 0, 0];
    ranked.forEach((r, i) => (handPoints[r.seat] = scoreHand({ position: i + 1, category: r.rank.category, isWinner: false }).total));
    // From the moment the hands are shown until the results close, this hand's peeks can be bought.
    this.peekWindow = { hand: handNumber, cards: peekCards.map((c) => (c ? { ...c } : null)), hands: hands.map((h) => h.map((c) => ({ ...c }))), winnerSeat: winner.seat, pending: handPoints };
    if (this.observer?.onHandReveal) {
      const pre: HandRecord = {
        handNumber, dealerSeat: this.dealerSeat, targetState: this.cfg.stateMethod === "RANDOM" ? fixedTarget ?? this.pool[0] : "",
        winnerId: winnerTotals.id, winnerSeat: winner.seat, winningCategory: winner.rank.category, knockerSeat, knockRound,
        results: ranked.map((r, i) => ({ id: this.cfg.players[r.seat].id, seat: r.seat, cards: r.cards, rank: r.rank, position: i + 1,
          breakdown: scoreHand({ position: i + 1, category: r.rank.category, isWinner: false }) })),
      };
      await this.observer.onHandReveal({ record: pre, board: board() });
    }
    if (this.cfg.stateMethod === "RANDOM") {
      wonState = fixedTarget ?? this.pool[0];
    } else {
      const ctrl = this.controllers[winner.seat];
      const chosen = ctrl.chooseState
        ? await ctrl.chooseState([...this.pool], [...winnerTotals.states], { handNumber, table: table?.(), handPoints })
        : this.pool[0];
      wonState = this.pool.includes(chosen) ? chosen : this.pool[0];
    }
    const idx = this.pool.indexOf(wonState);
    if (idx >= 0) this.pool.splice(idx, 1);

    const ownedBefore = [...winnerTotals.states];
    const results: PlayerHandResult[] = ranked.map((r, i) => {
      const isWinner = r.seat === winner.seat;
      const breakdown = scoreHand({
        position: i + 1,
        category: r.rank.category,
        isWinner,
        wonState: isWinner ? wonState : undefined,
        ownedBefore,
      });
      this.totals[r.seat].score += breakdown.total;
      return {
        id: this.cfg.players[r.seat].id,
        seat: r.seat,
        cards: r.cards,
        rank: r.rank,
        position: i + 1,
        breakdown,
      };
    });
    winnerTotals.states.push(wonState);

    const record: HandRecord = {
      handNumber,
      dealerSeat: this.dealerSeat,
      targetState: wonState,
      winnerId: winnerTotals.id,
      winnerSeat: winner.seat,
      winningCategory: winner.rank.category,
      knockerSeat,
      knockRound,
      results,
    };
    this.history.push(record);
    for (const c of this.controllers) c.onHandEnd?.(record);

    this.peekWindow!.pending = null;   // the hand's points are in the totals now
    // A peek bought before a resume (this same hand, played again) is charged again — it was paid for.
    for (const p of this.peeks) if (p.hand === handNumber && !p.charged) this.totals[p.seat].score -= PEEK_COST;
    for (const p of this.peeks) p.charged = true;
    await this.observer?.onShowdown?.({ record, board: board() });
    await this.observer?.onStateWon?.({ seat: winner.seat, state: wonState, board: board() });
    this.peekWindow = null;
  }
}

/**
 * The move checks below are shared by the engine and the AI's simulator so both play by the same rules.
 * While the middle is face down the only moves are turning it over or (not the dealer) taking it blind. */
export function mustReveal(a: Action, revealed: boolean, dealer: boolean): boolean {
  return !revealed && !(a.type === "TAKE_MIDDLE" && !dealer);
}

/** Turn an illegal action (after any reveal has happened) into the nearest legal one.
 *  Whoever has just turned the middle over may not take all five on that turn (`justRevealed`). */
export function legalize(a: Action, t: { revealed: boolean; knocked: boolean; dealer?: boolean; justRevealed?: boolean }): Action {
  if (a.type === "REVEAL") a = { type: "KEEP" };
  if (a.type === "TAKE_MIDDLE" && t.revealed && t.justRevealed) return { type: "KEEP" };
  if (a.type === "KNOCK" && (!t.revealed || t.knocked)) return { type: "KEEP" };
  if (a.type === "TAKE_MIDDLE" && !t.revealed && t.dealer) return { type: "KEEP" };
  if (a.type === "SWAP_ONE" && !t.revealed) return { type: "KEEP" };
  return a;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n | 0));
}

export function stateName(code: string): string {
  return STATE_BY_CODE.get(code)?.name ?? code;
}

export { cardToString };
